import { DownloadOutlined } from "@ant-design/icons";
import type { CheckboxProps } from "antd";
import { Alert, Checkbox, Divider, Modal } from "antd";
import { useExportTableDataMutation } from "components";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { showInfoNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { useGetTransferableEntitiesQuery } from "../../services/server/ConfigurationTransferApi";
import { TransferableEntitiesTree } from "../trees/TransferableEntitiesTree";

export interface ExportTablesSelectionModalProps {
    buttonTextKey?: string;
}

export const ExportTablesSelectionModal: FC<ExportTablesSelectionModalProps> = ({ buttonTextKey = "button.exportConfig" }) => {
    const dataGroup = getAssignedDataGroup();
    const dispatch = useDispatch();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [exportAll, setExportAll] = useState(true);
    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

    const {
        data: entitiesData,
        isLoading,
        error,
        // @ts-ignore
    } = useGetTransferableEntitiesQuery(dataGroup?.id ?? 0, {
        skip: !isModalVisible || !dataGroup?.id,
    });

    const [exportDataRequest, { error: exportDataError }] = useExportTableDataMutation();

    const notifyInfo = useCallback(() => {
        dispatch(
            showInfoNotification({
                text: i18n.t("info.export"),
                autoCloseDisabled: true,
            }),
        );
    }, [dispatch]);

    const showModal = () => {
        setIsModalVisible(true);
        setExportAll(true);
        setCheckedKeys([]);
    };

    const handleClose = () => {
        setIsModalVisible(false);
        setExportAll(true);
        setCheckedKeys([]);
    };

    const handleExportAllChange: CheckboxProps["onChange"] = (e) => {
        const checked = e.target.checked;
        setExportAll(checked);

        if (checked) {
            setCheckedKeys([]);
        }
    };

    const handleTreeCheck = (checked: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {
        const keys = Array.isArray(checked) ? checked : checked.checked;
        // Filter out dependency nodes (they have special key format)
        const validKeys = keys.filter((key) => !key.toString().includes("-dep-"));
        setCheckedKeys(validKeys);
    };

    const handleExport = async () => {
        const baseUrl = `${ResourceName.DATA_GROUPS}/${dataGroup.id}/config-export`;
        let url = baseUrl;

        if (exportAll) {
            // When "Export All" is checked, export all transferable entities
            if (entitiesData && entitiesData.length > 0) {
                const tableNames = entitiesData.map((entity) => entity.tableName).join(",");
                url = `${baseUrl}?tables=${tableNames}`;
            }
        } else {
            // When specific tables are selected
            if (checkedKeys.length === 0) {
                return;
            }

            const tableNames = checkedKeys.map((key) => key.toString()).join(",");
            url = `${baseUrl}?tables=${tableNames}`;
        }

        exportDataRequest({
            url,
            method: "GET",
        });

        if (!exportDataError) {
            notifyInfo();
        }
        handleClose();
    };

    const isExportDisabled = !exportAll && checkedKeys.length === 0;

    return (
        <>
            <SecondaryButton onClick={showModal} icon={<DownloadOutlined />}>
                {i18n.t(buttonTextKey)}
            </SecondaryButton>

            <Modal
                title={i18n.t("configurationTransfer.modal.title")}
                open={isModalVisible}
                onCancel={handleClose}
                width={800}
                footer={[
                    <SecondaryButton key="close" onClick={handleClose}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    <PrimaryButton key="export" onClick={handleExport} disabled={isExportDisabled}>
                        {i18n.t("button.export")}
                    </PrimaryButton>,
                ]}
            >
                {/* Export All Checkbox */}
                <Checkbox checked={exportAll} onChange={handleExportAllChange} style={{ marginBottom: 16 }}>
                    <strong>{i18n.t("configurationTransfer.modal.exportAll")}</strong>
                </Checkbox>

                {exportAll && (
                    <Alert
                        message={i18n.t("configurationTransfer.modal.exportAllInfo")}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Divider />

                {/* Transferable Entities Tree */}
                {error ? (
                    <Alert
                        message={i18n.t("core.notifications.error.title")}
                        description={i18n.t("configurationTransfer.error.loadEntities")}
                        type="error"
                        showIcon
                    />
                ) : (
                    <TransferableEntitiesTree
                        entities={entitiesData || []}
                        checkedKeys={checkedKeys}
                        onCheck={handleTreeCheck}
                        disabled={exportAll}
                        loading={isLoading}
                    />
                )}

                {/* Validation Warning */}
                {!exportAll && checkedKeys.length === 0 && (
                    <Alert
                        message={i18n.t("configurationTransfer.validation.noTablesSelected")}
                        type="warning"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                )}
            </Modal>
        </>
    );
};
